import { useState, useEffect, type ReactNode } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { AuthContext, type User } from './authStore'

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)

  // Configurer axios avec le token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }, [token])

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.data?.code === 'SEASON_ACCESS_REQUIRED' && localStorage.getItem('token')) {
          localStorage.removeItem('token')
          setToken(null)
          setUser(null)
          toast.error(error.response.data.message)
        }
        return Promise.reject(error)
      }
    )

    return () => axios.interceptors.response.eject(interceptor)
  }, [])

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/auth/verify')
          setUser(response.data.user)
        } catch (error) {
          console.error('Token invalide:', error)
          localStorage.removeItem('token')
          setToken(null)
          setUser(null)
        }
      }
      setLoading(false)
    }

    verifyToken()
  }, [token])

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      })

      const { user: userData, token: newToken } = response.data
      
      setUser(userData)
      setToken(newToken)
      localStorage.setItem('token', newToken)
      
      toast.success('Connexion réussie !')
      return userData
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erreur lors de la connexion'
      toast.error(message)
      throw error
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('token')
    toast.success('Déconnexion réussie')
  }

  const updateUser = (userData: Partial<User>) => {
    setUser(prevUser => prevUser ? { ...prevUser, ...userData } : null)
  }

  const value = {
    user,
    token,
    login,
    logout,
    updateUser,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
